import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Heart, Star, CheckCircle2, ExternalLink } from 'lucide-react';
import { apiFetch } from '../../api/client';
import './SupportProject.css';

function errorText(error: unknown) {
  const message = error instanceof Error ? error.message : '';
  try { return JSON.parse(message.slice(message.indexOf('{'))).error as string; } catch { return 'Не удалось открыть оплату. Попробуйте позже.'; }
}

export function SupportProject() {
  const nav = useNavigate();
  const [amount, setAmount] = useState('100');
  const [invoice, setInvoice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState<'idle' | 'pending' | 'paid' | 'refunded'>('idle');
  const [message, setMessage] = useState('');
  const [confirmedAmount, setConfirmedAmount] = useState(0);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const n = Number(amount);
  const valid = /^\d+$/.test(amount) && Number.isSafeInteger(n) && n >= 1;

  async function check(id: string) {
    const result = await apiFetch<{ status: 'pending' | 'paid' | 'refunded'; amount: number }>('/api/donations/' + id);
    if (!active.current) return;
    if (result.status !== 'pending') setState(result.status);
    if (result.status === 'paid') { setConfirmedAmount(result.amount); setMessage(''); }
    if (result.status === 'refunded') setMessage('Звёзды возвращены. Спасибо за внимание к проекту!');
  }

  useEffect(() => {
    if (!invoice || state !== 'pending') return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (++attempts > 90) { window.clearInterval(timer); return; }
      void check(invoice).catch(() => {});
    }, 2000);
    return () => window.clearInterval(timer);
  }, [invoice, state]);

  async function donate() {
    if (!valid || busy || state === 'pending') return;
    setBusy(true); setMessage('');
    try {
      const created = await apiFetch<{ id: string; url: string }>('/api/donations/invoice', {
        method: 'POST', body: JSON.stringify({ amount: n }),
      });
      if (!active.current) return;
      setInvoice(created.id);

      // Inside Telegram we retain the native payment sheet. On desktop the same
      // invoice is opened in Telegram Web/Desktop (or a browser tab) and status
      // is confirmed by polling the platform API.
      const tg = (window as any)?.Telegram?.WebApp;
      if (tg?.initData && typeof tg.openInvoice === 'function') {
        tg.openInvoice(created.url, (status: string) => {
          if (!active.current) return;
          setBusy(false);
          if (status === 'paid' || status === 'pending') {
            setState('pending');
            setMessage('Ждём подтверждения оплаты от Telegram. Повторно платить не нужно.');
            void check(created.id).catch(() => {});
          } else {
            setState('idle');
            setMessage(status === 'cancelled'
              ? 'Оплата отменена. Можно вернуться к поддержке в любое время.'
              : 'Оплата не завершена. Если звёзды списались, проверьте статус перед повторной попыткой.');
            void check(created.id).catch(() => {});
          }
        });
        return;
      }

      const popup = window.open(created.url, '_blank', 'noopener,noreferrer');
      setBusy(false);
      setState('pending');
      setMessage(popup
        ? 'Счёт открыт в новой вкладке. Завершите оплату в Telegram — статус обновится здесь автоматически.'
        : 'Браузер заблокировал новую вкладку. Разрешите всплывающие окна и нажмите кнопку ниже.');
    } catch (e) {
      if (active.current) { setBusy(false); setMessage(errorText(e)); }
    }
  }

  return (
    <div className="rf-detail support-page">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => nav('/settings')}><ChevronLeft /> Назад</button>
        <div className="rf-topbar-title">Поддержать проект</div>
      </div>
      <div className="rf-detail-scroll">
        <div className="support-wrap">
          <div className="rf-card support-card">
            <div className="support-icon"><Heart size={28} aria-hidden="true" /></div>
            <h1>Спасибо, что вы с нами</h1>
            <p>«Очередной агент» развивается благодаря людям, которым он полезен. Ваша поддержка помогает улучшать платформу и создавать новые возможности.</p>
            <p>Будем рады каждой звезде — от 1 ⭐. Это добровольно: поддержка не меняет лимиты и не открывает дополнительные функции.</p>
            {state === 'paid' ? (
              <div className="support-success" role="status">
                <CheckCircle2 size={32} aria-hidden="true" />
                <h2>Спасибо за {confirmedAmount.toLocaleString('ru-RU')} ⭐!</h2>
                <p>Оплата подтверждена. Вы помогаете «Очередному агенту» становиться лучше 💛</p>
                <button className="support-primary" onClick={() => { setState('idle'); setInvoice(null); }}>Поддержать ещё раз</button>
              </div>
            ) : (
              <>
                <label className="support-label" htmlFor="star-amount">Количество звёзд</label>
                <div className="support-input-wrap">
                  <Star size={22} aria-hidden="true" />
                  <input id="star-amount" type="text" inputMode="numeric" autoComplete="off" value={amount} disabled={busy || state === 'pending'} aria-describedby="star-help" aria-invalid={!valid} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="support-presets" aria-label="Быстрый выбор суммы">
                  {[50, 100, 250, 500, 1000].map((a) => <button key={a} disabled={busy || state === 'pending'} aria-pressed={n === a} onClick={() => setAmount(String(a))}>{a} ⭐</button>)}
                </div>
                <p id="star-help" className="support-help">{valid ? 'Введите свою сумму или выберите выше. Если Telegram ограничит сумму одного платежа, её можно разделить на несколько.' : 'Введите целое количество звёзд от 1. Очень большую сумму можно разделить на несколько платежей.'}</p>
                <button className="support-primary" disabled={!valid || busy || state === 'pending'} onClick={() => void donate()}>{busy ? 'Создаём счёт…' : state === 'pending' ? 'Подтверждаем оплату…' : `Поддержать${valid ? ' на ' + n.toLocaleString('ru-RU') + ' ⭐' : ''}`}</button>
                <p className="support-help">Разовая поддержка, без подписки и автосписаний. Перед оплатой Telegram покажет точную сумму.</p>
              </>
            )}
            {message && <div className="support-message" role="status">{message}</div>}
            {invoice && state !== 'paid' && (
              <div className="support-invoice-actions">
                <button className="support-secondary" onClick={() => void check(invoice).catch(() => setMessage('Не удалось проверить статус. Попробуйте позже.'))}>Проверить статус оплаты</button>
              </div>
            )}
          </div>
          <div className="support-footer">
            <ExternalLink size={18} aria-hidden="true" />
            <p>Вопрос по оплате или возврату? Напишите через форму обратной связи и укажите дату и сумму платежа.</p>
            <button className="support-secondary" onClick={() => nav('/settings/feedback')}>Связаться с командой</button>
          </div>
        </div>
      </div>
    </div>
  );
}
